import { useEffect, useRef } from "react";
import { Button } from "../../components/ui/Button";
import { ColorField } from "./ColorField";

type OptionalColorFieldProps = {
	label: string;
	/** The label with its group, for screen readers: "Gift popup background". */
	name: string;
	/** Null follows the shop's theme. */
	value: string | null;
	startingColor: string;
	onChange: (value: string | null) => void;
};

/**
 * Unset is a real choice here, not an empty box: it means the theme's colour,
 * which changes with the theme, so it has its own state rather than a hex.
 */
export const OptionalColorField = ({
	label,
	name,
	value,
	startingColor,
	onChange,
}: OptionalColorFieldProps) => {
	const root = useRef<HTMLDivElement>(null);
	const toggled = useRef(false);
	const isSet = value !== null;

	// Switching state swaps out the button that was pressed, so focus moves to
	// the first control of the new state instead of falling back to the page.
	useEffect(() => {
		if (!toggled.current) return;
		toggled.current = false;
		root.current?.querySelector<HTMLElement>("input, button")?.focus();
	}, [isSet]);

	const toggle = (next: string | null) => {
		toggled.current = true;
		onChange(next);
	};

	return (
		<div ref={root}>
			{value === null ? (
				<div className="space-y-1.5">
					<span className="block text-sm text-ink">{label}</span>
					<span className="flex h-9 items-center gap-2">
						<span className="h-9 w-10 shrink-0 rounded-md border border-dashed border-line-strong" />
						<span className="flex-1 text-sm text-ink-muted">Follows your theme</span>
						<Button
							variant="outline"
							size="sm"
							aria-label={`Choose ${name.toLowerCase()} colour`}
							onClick={() => toggle(startingColor)}
						>
							Choose
						</Button>
					</span>
				</div>
			) : (
				<div className="flex items-end gap-2">
					<div className="flex-1">
						<ColorField label={label} name={name} value={value} onChange={onChange} />
					</div>
					<Button
						variant="ghost"
						size="sm"
						className="mb-0.5"
						aria-label={`Use the theme's ${name.toLowerCase()} colour`}
						onClick={() => toggle(null)}
					>
						Use theme
					</Button>
				</div>
			)}
		</div>
	);
};
